/**
 * `<page-policy-editor>` — the editor shell, routed at
 * `/instances/:id/policies/new` and `/instances/:id/policies/:kind/:name`.
 *
 * It is the common chrome of `03` §3.1: **left column authored, right column
 * derived.** The left holds the envelope and the kind's body; the right holds the
 * rendered YAML and the linter's findings. The object is addressed by
 * `(kind, name)` — Sluice's own uniqueness key across the whole policy directory —
 * so a bookmarked policy URL survives a re-import.
 *
 * Implements `SC-F502` (the shared envelope, with `apiVersion` derived and never
 * offered), `SC-F511` and `SC-F512` through the two kind editors it hosts, and
 * `SC-F527` — the enforcement-mode switch and the shadow rollout. Findings arrive
 * with every read and every save (`SC-F529`) and are anchored to fields by path, so
 * the rung that blocks a save and the rung that blocks only a publish are visibly
 * different things.
 *
 * Three decisions worth stating:
 *
 *  - **A mode change is not a side effect of saving.** Promoting out of a shadow
 *    mode starts changing real query outcomes, and the API gates that on an
 *    acknowledgement carrying the observation evidence — on the envelope patch. So
 *    this surface routes every mode change through that path, behind the confirm
 *    dialog `SC-S522` asks for, rather than letting a save about a table pattern
 *    promote a policy in passing.
 *  - **The YAML is a rendering of what is stored**, not of what is on screen. There
 *    is no client-side renderer and there must not be one: the bundle digest is a
 *    function of the server's bytes. The pane says which state it reflects.
 *  - **The Test pane runs against what is on screen** (`SC-F708`), which is the one
 *    place this surface deliberately differs from the YAML pane above it. The
 *    control plane reads the stored draft, layers the unsaved object onto it,
 *    renders a transient bundle and mounts it for one run; nothing is saved and no
 *    report is filed against a generation. The response's own notice and its
 *    `SLUICE-U04` caveat are rendered verbatim, because the compile happened in a
 *    separate container and not in the process serving the instance — a green run
 *    is a statement about the draft plus these edits, never about what is live.
 *  - **A kind whose editor lives elsewhere is not faked.** `DataSource` and
 *    `SubjectBinding` have their own surfaces because their real artifacts are a
 *    sealed credential and a key lifecycle; a kind this surface does not yet author
 *    says so instead of rendering an empty form.
 *
 * Stories: `SC-S502`, `SC-S503`–`SC-S507`, `SC-S509`, `SC-S511`–`SC-S513`,
 * `SC-S522`, `SC-S523`.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  enforcementModeVariant,
  toast,
  type BnSelectChangeDetail,
  type BnSelectOption,
  type BnTab,
  type BnTabsChangeDetail,
} from "../components/index.js";

import {
  listTestSuites,
  runTests,
  type ObjectRef,
  type TestCaseResult,
  type TestRunResult,
  type TestSuite,
} from "../api/cli.js";
import { ApiError, parseError, validationFields } from "../api/errors.js";
import { isTerminal, jobFailure, watchJob, type Job } from "../api/jobs.js";
import {
  createPolicy,
  deletePolicy,
  getPolicyWithETag,
  getPolicyYaml,
  getShadowStatus,
  listPolicies,
  patchPolicy,
  replacePolicy,
  type EnforcementMode,
  type ObjectMeta,
  type PolicyFinding,
  type PolicyKind,
  type PolicyModeChange,
  type PolicyObject,
  type PolicyShadowReport,
} from "../api/policies.js";
import { hrefFor, navigate } from "../nav.js";

import { loadBindings, type BindingSummary } from "./policy/bindings.js";
import "./policy/editor-rowfilter.js";
import "./policy/editor-sqlaccess.js";
import type { SpecChangeDetail } from "./policy/editor-sqlaccess.js";
import { asRecord, clone, type RowFilterSpec, type SqlAccessSpec } from "./policy/model.js";
import "./policy/policy-envelope.js";
import type { EnvelopeChangeDetail, OrderPeer } from "./policy/policy-envelope.js";
import "./policy/policy-findings.js";
import { countFindings, type PolicyFindingClickDetail } from "./policy/policy-findings.js";
import { loadFoldMembers } from "./policy/policy-fold.js";
import { loadPolicySchema, type PolicySchema } from "./policy/schema.js";
import { EMPTY_CACHE, loadSchemaCache, type SchemaCache } from "./policy/schema-cache.js";
import type { FoldMember } from "./policy/sql-preview.js";

/** The kinds this surface authors. Every other kind is real and is authored
 * somewhere else, or not yet — and the picker says which, rather than opening a form
 * that cannot save. */
const HOSTED_KINDS: readonly PolicyKind[] = ["SqlAccessPolicy", "RowFilterPolicy"];

/** The two kinds whose editor is a different surface entirely, because their real
 * artifact is not a policy form: a datasource write is three rows in one transaction
 * including a sealed credential, and a SubjectBinding is where instance API keys are
 * minted. */
const ELSEWHERE: Partial<Record<PolicyKind, { label: string; path: (id: string) => string }>> = {
  DataSource: {
    label: "Datasources have their own surface: a datasource write also seals a credential and records a schema pointer.",
    path: (id) => hrefFor("instances", id, "datasources", "new"),
  },
  SubjectBinding: {
    label: "Subject bindings have their own surface, because that is where instance API keys are minted.",
    path: (id) => hrefFor("instances", id, "subject-bindings", "new"),
  },
};

@customElement("page-policy-editor")
export class PagePolicyEditor extends LitElement {
  @property() instanceId = "";
  @property({ type: Boolean }) create = false;
  @property() kind: PolicyKind | "" = "";
  @property() name = "";

  @state() private schema: PolicySchema | null = null;
  @state() private loading = true;
  @state() private error: ApiError | null = null;

  /** The stored record, or null while creating. */
  @state() private object: PolicyObject | null = null;
  @state() private etag = "";

  /** The kind being authored. On create it comes from `?kind=` or the picker. */
  @state() private activeKind: PolicyKind | "" = "";
  @state() private metadata: ObjectMeta = { name: "" };
  @state() private mode = "";
  @state() private spec: Record<string, unknown> = {};

  /** Findings: the server's, plus the field faults a refused save reported. */
  @state() private findings: PolicyFinding[] = [];

  @state() private cache: SchemaCache = EMPTY_CACHE;
  @state() private bindings: BindingSummary[] = [];
  @state() private bindingTotal = 0;
  @state() private peers: OrderPeer[] = [];
  @state() private foldMembers: FoldMember[] = [];

  @state() private yaml = "";
  @state() private saving = false;
  @state() private dirty = false;
  @state() private pane = "yaml";

  /** The promotion dialog's state (`SC-S522`). */
  @state() private promoteOpen = false;
  @state() private shadow: PolicyShadowReport | null = null;
  @state() private promoteTo: EnforcementMode = "Enforce";
  @state() private promoteReason = "";
  @state() private promoting = false;

  @state() private deleteOpen = false;

  /**
   * `SC-F708`'s pane: run a stored suite against the editor's UNSAVED state.
   *
   * The suites are loaded lazily, the first time the pane is opened, because a
   * page load must not fetch what nobody looked at — and because the pane is the
   * only thing on this screen that costs a container start when used.
   */
  @state() private testSuites: TestSuite[] = [];
  @state() private testSuitesLoaded = false;
  @state() private testSuitesLoading = false;
  @state() private testSuitesError: ApiError | null = null;
  @state() private testSuiteId = "";
  @state() private testRunning = false;
  @state() private testJob: Job<TestRunResult> | null = null;
  @state() private testResult: TestRunResult | null = null;
  @state() private testError: ApiError | null = null;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 24rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
      overflow-wrap: anywhere;
    }
    .crumb {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .head-actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .columns {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-lg);
      align-items: flex-start;
    }
    .authored {
      flex: 1 1 32rem;
      min-width: 0;
    }
    .derived {
      flex: 1 1 24rem;
      min-width: 0;
      position: sticky;
      top: var(--bino-space-md);
    }
    section {
      margin-bottom: var(--bino-space-xl);
    }
    h2 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .error-message {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      max-width: 70ch;
    }
    .error-actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
    .pane {
      padding-top: var(--bino-space-md);
    }
    .verdict {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-base);
      max-width: 80ch;
    }
    .verdict.blocked {
      color: var(--bino-error);
    }
    .dirty {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border-radius: var(--bino-radius-sm);
      padding: 0 var(--bino-space-xs);
    }
    .kv {
      margin-top: var(--bino-space-md);
    }
    p {
      max-width: 80ch;
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .promote p {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-base);
    }
    .test-run {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-md);
    }
    .warn {
      margin: 0 0 var(--bino-space-sm);
      padding: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius-sm);
    }
    .cases {
      list-style: none;
      margin: var(--bino-space-md) 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
    .cases li {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-xs);
    }
    .case-name {
      font-size: var(--bino-font-size-sm);
      overflow-wrap: anywhere;
    }
    .raw {
      flex: 1 1 100%;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-xs);
      overflow-x: auto;
    }
    .caveats {
      margin: var(--bino-space-md) 0 0;
      padding-left: var(--bino-space-md);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
    }
  `;

  /**
   * Everything is loaded from `willUpdate` and nothing from `connectedCallback`: the
   * router sets `.instanceId`, `.kind` and `.name` as properties, so the element
   * exists before it knows what it is editing. Booting in both places would issue
   * every request twice on every navigation.
   */
  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has("instanceId") || changed.has("kind") || changed.has("name")) {
      void this.boot();
    }
  }

  /** boot loads everything the editor needs, in the order the form needs it: the
   * schema first, because nothing renders without it, then the object, then the three
   * advisory sources that only make previews better. */
  private async boot() {
    if (!this.instanceId) return;
    this.loading = true;
    this.error = null;
    try {
      const { schema } = await loadPolicySchema();
      this.schema = schema;
    } catch (err) {
      // Without the schema there are no enums, no requiredness and no honest-label
      // statuses, so there is no form to render. This is the one load failure that is
      // fatal to the page rather than degrading it.
      this.error = parseError(err);
      this.loading = false;
      return;
    }

    this.activeKind = this.create ? this.kindFromQuery() : this.kind;

    if (!this.create) {
      try {
        const { value, etag } = await getPolicyWithETag(this.instanceId, this.kind as PolicyKind, this.name);
        this.adopt(value, etag);
      } catch (err) {
        this.error = parseError(err);
        this.loading = false;
        return;
      }
      void this.loadYaml();
    } else {
      this.metadata = { name: "", priority: 0 };
      this.mode = String(
        this.schema?.initial(this.enforcementField) ?? this.schema?.enumDefault("EnforcementMode") ?? "",
      );
      this.spec = this.freshSpec(this.activeKind);
      this.findings = [];
    }

    this.loading = false;
    void this.loadAdvisory();
  }

  /** The `?kind=` the list page hands over. The kind is chosen inside this page
   * rather than being part of the identity of a record that does not exist yet. */
  private kindFromQuery(): PolicyKind | "" {
    const raw = new URLSearchParams(location.search).get("kind") ?? "";
    return (this.schema?.doc.kinds ?? []).some((k) => k.kind === raw) ? (raw as PolicyKind) : "";
  }

  private get enforcementField() {
    return (
      this.schema?.field("policyCommon", "enforcementMode") ??
      this.schema?.field("sqlAccessSpec", "enforcementMode")
    );
  }

  /**
   * adopt makes a record the form's state.
   *
   * `etag` is the object's content digest. The server writes it as `"<digest>"` and
   * accepts either spelling on `If-Match`, so a digest read straight off a response
   * body is a valid tag — which is what lets a save avoid a second read.
   */
  private adopt(value: PolicyObject, etag: string) {
    this.object = value;
    this.etag = etag;
    this.activeKind = value.kind;
    this.metadata = clone(value.metadata);
    const spec = clone(asRecord(value.spec));
    this.mode = typeof spec["enforcementMode"] === "string" ? (spec["enforcementMode"] as string) : "";
    this.spec = spec;
    this.findings = value.findings ?? [];
    this.dirty = false;
  }

  /** A fresh spec for a kind. Only the fields Sluice requires are seeded, plus the
   * schema's `initial` where it deliberately differs from Sluice's default — which is
   * exactly `enforcementMode`, so a new policy is observable before it decides
   * anything. */
  private freshSpec(kind: PolicyKind | ""): Record<string, unknown> {
    const initialMode = this.schema?.initial(this.enforcementField);
    const base: Record<string, unknown> = {};
    if (typeof initialMode === "string" && initialMode !== "") base["enforcementMode"] = initialMode;
    base["match"] = {};
    if (kind === "RowFilterPolicy") base["filter"] = {};
    return base;
  }

  private async loadYaml() {
    if (this.create || !this.activeKind) return;
    try {
      this.yaml = await getPolicyYaml(this.instanceId, this.activeKind, this.name);
    } catch {
      // The YAML pane is a read of the stored object. Losing it costs the pane, not
      // the editor, and the pane says so.
      this.yaml = "";
    }
  }

  /** The three advisory sources: the schema cache for pattern previews and column
   * pickers, the bindings for the template picker, and the same-kind siblings for the
   * ordering and fold previews. Every one of them degrades to "unknown" rather than
   * failing the page. */
  private async loadAdvisory() {
    this.cache = await loadSchemaCache(this.instanceId);
    try {
      const { bindings, total } = await loadBindings(this.instanceId);
      this.bindings = bindings;
      this.bindingTotal = total;
    } catch {
      this.bindings = [];
      this.bindingTotal = 0;
    }
    if (!this.activeKind) return;
    try {
      const list = await listPolicies(this.instanceId, { kind: this.activeKind, limit: 200 });
      this.peers = list.items
        .filter((r) => r.enabled)
        .map((r) => ({ name: r.name, priority: r.priority, shadow: !!r.shadow }));
    } catch {
      this.peers = [];
    }
    if (this.activeKind === "RowFilterPolicy") {
      try {
        this.foldMembers = await loadFoldMembers(this.instanceId, this.metadata.name);
      } catch {
        this.foldMembers = [];
      }
    }
  }

  // ── Editing ─────────────────────────────────────────────────────────────

  private onEnvelope(e: CustomEvent<EnvelopeChangeDetail>) {
    e.stopPropagation();
    this.metadata = e.detail.metadata;
    if (this.create) {
      this.mode = e.detail.mode;
      this.spec = { ...this.spec, enforcementMode: e.detail.mode };
    }
    this.dirty = true;
  }

  private onSpec(e: CustomEvent<SpecChangeDetail<Record<string, unknown>>>) {
    e.stopPropagation();
    this.spec = e.detail.spec;
    this.dirty = true;
  }

  /** Switching the kind on create replaces the spec: two kinds share the envelope
   * and nothing else, so carrying a body across would mean posting fields the new
   * kind does not model — which the server refuses as unknown fields, correctly. */
  private onKind(e: CustomEvent<BnSelectChangeDetail>) {
    e.stopPropagation();
    const next = e.detail.value as PolicyKind;
    const elsewhere = ELSEWHERE[next];
    if (elsewhere) {
      navigate(elsewhere.path(this.instanceId));
      return;
    }
    this.activeKind = next;
    this.spec = this.freshSpec(next);
    this.findings = [];
    void this.loadAdvisory();
  }

  // ── Saving ──────────────────────────────────────────────────────────────

  private get blockingSave(): PolicyFinding[] {
    return this.findings.filter((f) => f.severity === "error");
  }

  private async save() {
    if (!this.activeKind) return;
    this.saving = true;
    try {
      const input = {
        kind: this.activeKind,
        metadata: this.metadata,
        spec: this.spec,
      };
      if (this.create) {
        const created = await createPolicy(this.instanceId, input);
        toast(`${created.kind} ${created.metadata.name} created in ${created.derived.enforcement_mode ?? "the draft"}`, "success");
        navigate(hrefFor("instances", this.instanceId, "policies", created.kind, created.metadata.name));
        return;
      }
      const saved = await replacePolicy(
        this.instanceId,
        this.activeKind,
        this.name,
        input,
        { ifMatch: this.etag },
      );
      // The digest IS the version — `config_objects` rows are immutable and
      // content-addressed — so the response body carries the next `If-Match` and no
      // re-read is needed. Re-reading would also open a window in which another
      // maintainer's write to the shared draft lands between the two requests and this
      // editor adopts a record it never saved.
      this.adopt(saved, saved.digest);
      void this.loadYaml();
      void this.loadAdvisory();
      toast(`${saved.kind} ${saved.metadata.name} saved`, "success");
    } catch (err) {
      this.absorb(err);
    } finally {
      this.saving = false;
    }
  }

  /**
   * absorb turns a refused write into findings anchored to fields.
   *
   * The two 422s mean different things and are kept apart: `validation_failed`
   * carries `details.fields` and is about the *request* — a name that does not match
   * the pattern, a priority out of range — while `policy_invalid` carries
   * `details.findings` and is about the *configuration*, which is the linter speaking.
   * Merging them into one bag would lose the distinction the user needs to know
   * whether to fix a value or rethink a rule.
   */
  private absorb(err: unknown) {
    const parsed = parseError(err);
    if (parsed.code === "validation_failed") {
      const fields = validationFields(err);
      this.findings = [
        ...fields.map((f) => ({
          severity: "error" as const,
          rule: "validation",
          path: f.path,
          message: f.reason,
          title: "This value cannot be stored",
        })),
        ...this.findings.filter((f) => f.rule !== "validation"),
      ];
      toast(parsed.message, "error");
      return;
    }
    if (parsed.code === "policy_invalid") {
      const raw = parsed.details?.["findings"];
      if (Array.isArray(raw)) this.findings = raw as PolicyFinding[];
      toast(parsed.message, "error");
      return;
    }
    if (parsed.code === "precondition_failed") {
      toast(parsed.message, "error");
      return;
    }
    toast(
      parsed.requestId ? `${parsed.message} (reference ${parsed.requestId})` : parsed.message,
      "error",
    );
  }

  private async deleteFromDraft() {
    if (!this.activeKind) return;
    try {
      await deletePolicy(this.instanceId, this.activeKind, this.name);
      toast(`${this.activeKind} ${this.name} removed from the draft`, "success");
      navigate(hrefFor("instances", this.instanceId, "policies"));
    } catch (err) {
      this.absorb(err);
    } finally {
      this.deleteOpen = false;
    }
  }

  /** The soft delete of `05` §3.11: in the generation, out of the render. It is the
   * honest alternative to deleting a policy while debugging it. */
  private async setEnabled(enabled: boolean) {
    if (!this.activeKind) return;
    try {
      const patched = await patchPolicy(this.instanceId, this.activeKind, this.name, { enabled }, {
        ifMatch: this.etag,
      });
      this.adopt(patched, patched.digest);
      toast(enabled ? "Included in the render again" : "Excluded from the render", "success");
    } catch (err) {
      this.absorb(err);
    }
  }

  // ── Enforcement mode (SC-F527) ───────────────────────────────────────────

  private async openPromote() {
    if (!this.activeKind) return;
    this.promoteOpen = true;
    this.promoteTo = this.object?.derived.shadow ? "Enforce" : "Audit";
    this.promoteReason = "";
    try {
      this.shadow = await getShadowStatus(this.instanceId, this.activeKind, this.name);
    } catch {
      // The evidence panel is a second read. Without it the dialog still works and
      // says the count is unknown, which is not the same as zero.
      this.shadow = null;
    }
  }

  private async confirmPromote() {
    if (!this.activeKind) return;
    this.promoting = true;
    try {
      const patched = await patchPolicy(
        this.instanceId,
        this.activeKind,
        this.name,
        { enforcementMode: this.promoteTo, acknowledge: true, reason: this.promoteReason || undefined },
        { ifMatch: this.etag },
      );
      this.adopt(patched, patched.digest);
      void this.loadYaml();
      this.promoteOpen = false;
      toast(`Enforcement mode is now ${this.promoteTo}. It takes effect on the next publish.`, "success");
    } catch (err) {
      this.absorb(err);
    } finally {
      this.promoting = false;
    }
  }

  /** The evidence panel. `unavailable` is rendered as unknown, never as zero: a zero
   * would fire the never-matched warning on every promotion for a reason that has
   * nothing to do with the policy. */
  private renderObservation(change: PolicyModeChange | undefined) {
    if (!change) {
      return html`<p>
        The observation could not be read, so the match count is unknown rather than zero.
      </p>`;
    }
    if (change.observation === "unavailable") {
      return html`<p>
        No query trail is available on this deployment, so how many requests this policy matched is
        <strong>unknown</strong> — not zero.
      </p>`;
    }
    if (change.observation === "not_applicable") {
      return html`<p>This transition needs no observation.</p>`;
    }
    const window = change.window_seconds
      ? ` over the last ${Math.round(change.window_seconds / 86400)} days`
      : "";
    if (change.never_matched) {
      return html`<p>
        This policy matched <strong>no request</strong>${window}. That usually means the matcher is
        wrong rather than that nobody tried: a pattern that matches nothing fails silently. Check the
        pattern preview before promoting.
      </p>`;
    }
    return html`<p>
      This policy matched <strong>${change.matched_count}</strong>
      ${change.matched_count === 1 ? "request" : "requests"}${window} while it changed no outcome.
    </p>`;
  }

  private renderPromoteModal() {
    const options = (this.schema?.options("EnforcementMode") ?? []).map((o) => o);
    const activating = this.object?.derived.shadow && this.promoteTo === "Enforce";
    return html`<bn-modal
      heading="Change enforcement mode"
      .width=${560}
      tone=${activating ? "destructive" : "default"}
      ?open=${this.promoteOpen}
      @close=${() => (this.promoteOpen = false)}
    >
      <div class="promote">
        ${this.renderObservation(this.shadow?.Observation)}
        <bn-field label="New mode">
          <bn-select
            .options=${options}
            .value=${this.promoteTo}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
              e.stopPropagation();
              this.promoteTo = e.detail.value as EnforcementMode;
            }}
          ></bn-select>
        </bn-field>
        <bn-field
          label="Reason (optional)"
          hint="Recorded on the audit event for this change — the answer to “why was this enforced on the 14th”."
        >
          <bn-input
            .value=${this.promoteReason}
            @input=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              this.promoteReason = e.detail.value;
            }}
          ></bn-input>
        </bn-field>
      </div>
      <p slot="consequence">
        ${activating
          ? "Enforce is the only mode that denies, filters, masks or holds anything. The change lands in the draft now and starts changing query outcomes when the draft is published — on the reload path, once the digest read-back confirms the new generation."
          : "A shadow mode evaluates the policy and records what would have happened without affecting the outcome. Audit and DryRun are behaviourally identical in this build."}
      </p>
      <bn-button slot="footer" @click=${() => (this.promoteOpen = false)}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant=${activating ? "danger" : "primary"}
        ?loading=${this.promoting}
        @click=${() => void this.confirmPromote()}
      >
        ${activating ? "Promote to Enforce" : `Set ${this.promoteTo}`}
      </bn-button>
    </bn-modal>`;
  }

  // ── SC-F708: test the unsaved state ─────────────────────────────────────

  /**
   * loadTestSuites reads the instance's stored suites, once, the first time the
   * pane is opened.
   *
   * A failure here is a pane-level error and not a page-level one: the editor and
   * the linter are unaffected by not knowing which suites exist, so degrading this
   * pane is honest and blanking the page would not be.
   */
  private async loadTestSuites() {
    if (this.testSuitesLoaded || this.testSuitesLoading || !this.instanceId) return;
    this.testSuitesLoading = true;
    this.testSuitesError = null;
    try {
      const page = await listTestSuites(this.instanceId);
      this.testSuites = page.items;
      // The gate's suites first, then by name, and the first one preselected: the
      // suite a publish would run is the one an author most likely wants to run now.
      const gated = this.testSuites.find((s) => s.enabled);
      this.testSuiteId = gated?.id ?? this.testSuites[0]?.id ?? "";
      this.testSuitesLoaded = true;
    } catch (err) {
      this.testSuitesError = parseError(err);
    } finally {
      this.testSuitesLoading = false;
    }
  }

  /**
   * runUnsaved runs the selected suite against the editor's unsaved state
   * (`SC-F708`).
   *
   * The overlay carries the SAME document `save()` would have posted — one
   * `{kind, metadata, spec}` value — so what is tested is what would be saved, not
   * a second serialization of the form. Everything else about the bundle comes from
   * the stored draft, read server-side when the run starts, so a draft a colleague
   * edited in the meantime is reflected rather than assumed away.
   */
  private async runUnsaved() {
    if (!this.activeKind || !this.testSuiteId) return;
    if (!this.metadata.name.trim()) {
      // The overlay addresses an object by `(kind, name)`; without a name there is
      // nothing to replace and nothing to add. Refused here so the message names the
      // field rather than arriving as a 422 about `draft.objects[0]`.
      toast("Give this object a name first: an unsaved run addresses it by kind and name.", "error");
      return;
    }
    this.testRunning = true;
    this.testError = null;
    this.testResult = null;
    this.testJob = null;
    try {
      const started = await runTests(
        this.instanceId,
        {
          suite_id: this.testSuiteId,
          draft: { objects: [{ kind: this.activeKind, metadata: this.metadata, spec: this.spec }] },
        },
        { waitSeconds: 20 },
      );
      this.testJob = started.job;
      const done = isTerminal(started.job.status)
        ? started.job
        : await watchJob<TestRunResult>(started.job.id, {
            waitSeconds: 20,
            onUpdate: (j) => (this.testJob = j),
          });
      this.testJob = done;
      const failure = jobFailure(done);
      if (failure) {
        this.testError = failure;
        return;
      }
      this.testResult = done.result ?? null;
    } catch (err) {
      this.testError = parseError(err);
    } finally {
      this.testRunning = false;
    }
  }

  /**
   * renderTestPane is `SC-F708`'s surface, in four states.
   *
   * The one thing it must never do is read as a statement about the instance. A
   * green run here means "the suite passes against the draft plus what is on
   * screen", and the response's own `unsaved.notice` and the `SLUICE-U04` caveat are
   * rendered rather than paraphrased — they are the API's words, so a client cannot
   * soften them.
   */
  private renderTestPane() {
    if (this.testSuitesLoading) {
      return html`<bn-spinner block size="24" label="Loading this instance's test suites"></bn-spinner>`;
    }
    if (this.testSuitesError) return this.renderPaneError(this.testSuitesError, () => void this.loadTestSuites());
    if (this.testSuitesLoaded && this.testSuites.length === 0) {
      return html`<bn-empty-state
        icon="⌸"
        heading="No test suites on this instance"
        message="Testing unsaved changes runs an existing suite against the draft plus what is on screen. Without a suite there is nothing to run, and the pre-publish test gate has nothing to gate on."
      >
        <bn-button
          slot="actions"
          variant="primary"
          @click=${() => navigate(hrefFor("instances", this.instanceId, "tests"))}
        >
          Create a suite
        </bn-button>
      </bn-empty-state>`;
    }
    return html`
      <p class="lede">
        Runs a stored suite against <strong>this draft plus the edits on screen</strong>, inside a
        one-shot container on this instance's pinned image. Nothing is saved and nothing is published.
      </p>
      <div class="test-run">
        <bn-field label="Suite">
          <bn-select
            .options=${this.testSuiteOptions()}
            .value=${this.testSuiteId}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
              e.stopPropagation();
              this.testSuiteId = e.detail.value;
            }}
          ></bn-select>
        </bn-field>
        <bn-button
          variant="primary"
          ?loading=${this.testRunning}
          ?disabled=${!this.testSuiteId || !this.activeKind}
          @click=${() => void this.runUnsaved()}
        >
          Run against unsaved changes
        </bn-button>
      </div>
      ${this.testRunning && !this.testResult
        ? html`<bn-spinner
            block
            size="24"
            label=${this.testJob ? `Running: ${this.testJob.status}` : "Starting a one-shot container"}
          ></bn-spinner>`
        : nothing}
      ${this.testError ? this.renderPaneError(this.testError, () => void this.runUnsaved()) : nothing}
      ${this.testResult ? this.renderTestResult(this.testResult) : nothing}
    `;
  }

  /** testSuiteOptions labels each suite with whether the publish gate runs it, so
   * the choice is informed rather than a list of names. */
  private testSuiteOptions(): BnSelectOption[] {
    return this.testSuites
      .filter((s) => !!s.id)
      .map((s) => ({
        value: s.id as string,
        label: `${s.name} — ${s.cases.length} case${s.cases.length === 1 ? "" : "s"}${s.enabled ? " (in the gate)" : ""}`,
      }));
  }

  /**
   * renderTestResult renders the verdict with every honesty requirement the API
   * carries.
   *
   * `total: 0` with `reason: "no_cases"` is RED, not empty: `policy test` exits 3
   * for a suite that matched no files as well as for one with failures, and an empty
   * pass there is exactly what makes a test gate worthless (`SC-S708`).
   */
  private renderTestResult(r: TestRunResult) {
    const verdict = r.ok
      ? "Passed against your unsaved changes."
      : r.reason === "no_cases"
        ? "Failed: the suite ran zero cases. That is a failure, not an empty pass."
        : r.reason === "bundle_invalid"
          ? "Failed: the bundle formed by your unsaved changes did not load or compile, so no case ran."
          : `Failed: ${r.failed} of ${r.total} case${r.total === 1 ? "" : "s"}.`;
    return html`
      <p class="verdict ${r.ok ? "" : "blocked"}">${verdict}</p>
      ${r.unsaved
        ? html`<p class="lede" role="note">${r.unsaved.notice}</p>
            <div class="kv">
              <bn-kv
                .rows=${[
                  { label: "Based on draft generation", value: String(r.unsaved.based_on) },
                  { label: "Transient bundle digest", value: r.unsaved.digest },
                  { label: "Added", value: refList(r.unsaved.added) },
                  { label: "Replaced", value: refList(r.unsaved.replaced) },
                  { label: "Omitted", value: refList(r.unsaved.omitted) },
                  { label: "Saved", value: r.unsaved.persisted ? "yes" : "no — nothing was written" },
                ]}
              ></bn-kv>
            </div>`
        : nothing}
      ${(r.warnings ?? []).map((w) => html`<p class="warn" role="alert">${w}</p>`)}
      <ul class="cases">
        ${r.cases.map(
          (c: TestCaseResult) => html`<li>
            <bn-badge variant=${c.passed ? "success" : "error"}>${c.passed ? "pass" : "fail"}</bn-badge>
            <span class="case-name">${c.name}</span>
            ${c.uses_request_facts
              ? html`<bn-badge variant="warning">uses request facts</bn-badge>`
              : nothing}
            ${(c.diffs ?? []).map((d) => html`<code class="raw">${d.raw}</code>`)}
          </li>`,
        )}
      </ul>
      ${r.cases.some((c) => !c.passed)
        ? html`<bn-button
            size="sm"
            @click=${() => navigate(hrefFor("instances", this.instanceId, "tests"))}
          >
            Open the full report view
          </bn-button>`
        : nothing}
      <ul class="caveats">
        ${r.caveats.map((c) => html`<li>${c}</li>`)}
      </ul>
    `;
  }

  /**
   * renderPaneError is the error state of this pane, with the two fields `SC-S208`
   * requires on every failure: the `apierr` code and the request id. They are shown
   * rather than logged because they are what a support conversation starts from.
   */
  private renderPaneError(err: ApiError, retry: () => void) {
    return html`<div role="alert">
      <p class="error-message">${err.message}</p>
      <bn-kv
        .rows=${[
          { label: "Error code", value: err.code },
          ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
        ]}
      ></bn-kv>
      <div class="error-actions">
        <bn-button variant="primary" @click=${retry}>Try again</bn-button>
      </div>
    </div>`;
  }

  // ── Panes ───────────────────────────────────────────────────────────────

  /** Scroll the control a finding points at into view. The YAML is read-only, so a
   * finding is never fixed in the text — it is fixed in the form. */
  private revealField(detail: PolicyFindingClickDetail) {
    if (!detail.path) return;
    // The form is composed of nested shadow roots, so a global selector cannot reach
    // the control. The honest affordance is to bring the authored column into view and
    // name the path; a deep focus hand-off would need every component to expose a
    // per-path anchor, which is a larger contract than one finding is worth.
    this.renderRoot.querySelector(".authored")?.scrollIntoView({ block: "start", behavior: "smooth" });
    toast(`The finding is on ${detail.path}`, "info");
  }

  private renderYamlPane() {
    if (this.create) {
      return html`<p class="lede">
        The manifest is rendered by the control plane from the stored record, so it appears once this
        object has been saved. There is no raw-YAML editing anywhere in the product: the structured
        record is the source of truth and the YAML is a read-only, exportable rendering of it.
      </p>`;
    }
    return html`
      <p class="lede">
        A read-only rendering of the <strong>stored</strong> record${this.dirty
          ? " — unsaved edits on the left are not in it yet"
          : ""}. Copy it into a repository if you want to; it is never an input.
      </p>
      ${this.yaml
        ? html`<bn-code
            language="yaml"
            label=${`Rendered ${this.activeKind} ${this.name}`}
            filename=${`${String(this.activeKind).toLowerCase()}-${this.name}.yaml`}
            .value=${this.yaml}
          ></bn-code>`
        : html`<p class="lede">The rendering could not be read. The editor is unaffected.</p>`}
    `;
  }

  private renderFindingsPane() {
    const counts = countFindings(this.findings);
    return html`
      <p class="verdict ${counts.errors > 0 ? "blocked" : ""}">
        ${counts.errors > 0
          ? "This object cannot be saved in its current shape."
          : counts.blocking > 0
            ? "This object saves, and the draft cannot be published while a blocking warning stands."
            : "Nothing here blocks a save or a publish."}
      </p>
      <policy-findings
        .findings=${this.findings}
        emptyText="The linter found nothing on this object. It runs on every save, and the pinned Sluice binary's own verdict still wins whenever the two disagree."
        @finding-click=${(e: CustomEvent<PolicyFindingClickDetail>) => {
          e.stopPropagation();
          this.revealField(e.detail);
        }}
      ></policy-findings>
      ${this.object ? this.renderRecordFacts() : nothing}
    `;
  }

  private renderRecordFacts() {
    const o = this.object;
    if (!o) return nothing;
    return html`<div class="kv">
      <bn-kv
        .rows=${[
          { label: "apiVersion", value: o.derived.api_version },
          { label: "Digest", value: o.digest },
          { label: "Change in draft", value: o.change ?? "unchanged" },
          ...(o.derived.snapshot_total
            ? [
                {
                  label: "Snapshot position",
                  value: `${o.derived.snapshot_position} of ${o.derived.snapshot_total}`,
                },
              ]
            : []),
          { label: "Schema version", value: String(o.spec_version) },
          { label: "Last modified", value: `${o.updated} by ${o.updated_by_label || o.updated_by}` },
        ]}
      ></bn-kv>
    </div>`;
  }

  // ── Body ────────────────────────────────────────────────────────────────

  private renderKindPicker() {
    if (!this.create) return nothing;
    const kinds = this.schema?.doc.kinds ?? [];
    const options: BnSelectOption[] = kinds.map((k) => {
      const elsewhere = ELSEWHERE[k.kind];
      const hosted = HOSTED_KINDS.includes(k.kind);
      const option: BnSelectOption = {
        value: k.kind,
        label: k.title || k.kind,
        description: k.help,
      };
      if (elsewhere) {
        option.description = elsewhere.label;
      } else if (!hosted) {
        option.disabled = true;
        option.disabledReason = `The editor for this kind is not on this surface yet (${k.feature}). Nothing here would be able to save it, so it is not offered rather than opened as an empty form.`;
      }
      return option;
    });
    return html`<section>
      <h2>Kind</h2>
      <p class="lede">
        The kind is fixed after creation: changing it is a delete plus a create, and the same name may
        be reused across kinds because uniqueness is per <code>(kind, name)</code>.
      </p>
      <bn-field label="Policy kind" required>
        <bn-select
          filterable
          .options=${options}
          .value=${this.activeKind}
          placeholder="Choose what this policy does…"
          @change=${this.onKind}
        ></bn-select>
      </bn-field>
    </section>`;
  }

  private renderBody(): TemplateResult | typeof nothing {
    const schema = this.schema;
    if (!schema || !this.activeKind) return nothing;
    switch (this.activeKind) {
      case "SqlAccessPolicy":
        return html`<policy-editor-sqlaccess
          .schema=${schema}
          .spec=${this.spec as unknown as SqlAccessSpec}
          .findings=${this.findings}
          .cache=${this.cache}
          .bindings=${this.bindings}
          @spec-change=${this.onSpec}
        ></policy-editor-sqlaccess>`;
      case "RowFilterPolicy":
        return html`<policy-editor-rowfilter
          .schema=${schema}
          .spec=${this.spec as unknown as RowFilterSpec}
          .findings=${this.findings}
          .cache=${this.cache}
          .bindings=${this.bindings}
          .bindingTotal=${this.bindingTotal}
          .foldMembers=${this.foldMembers}
          .selfName=${this.metadata.name}
          .selfPriority=${this.metadata.priority ?? 0}
          .selfShadow=${isShadow(this.mode, schema)}
          @spec-change=${this.onSpec}
        ></policy-editor-rowfilter>`;
      default: {
        const kindSchema = schema.kind(this.activeKind);
        return html`<bn-card heading="This kind is authored on another surface">
          <p class="lede">
            ${ELSEWHERE[this.activeKind]?.label ??
            `${kindSchema?.title ?? this.activeKind} is a real kind with a real editor (${kindSchema?.feature ?? ""}), and it is not this one. Rendering its fields here would produce a form that cannot save, so the surface says so instead.`}
          </p>
          <div class="error-actions">
            <bn-button @click=${() => navigate(hrefFor("instances", this.instanceId, "policies"))}>
              Back to policies
            </bn-button>
          </div>
        </bn-card>`;
      }
    }
  }

  private renderHeader() {
    const kindSchema = this.activeKind ? this.schema?.kind(this.activeKind) : undefined;
    const mode = this.object?.derived.enforcement_mode ?? this.mode;
    const enabled = this.object?.enabled ?? true;
    return html`<header>
      <div class="head-text">
        <span class="crumb">
          <a href=${hrefFor("instances", this.instanceId, "policies")}>Policies</a>
          ${this.activeKind ? html` · ${kindSchema?.title ?? this.activeKind}` : nothing}
        </span>
        <h1>${this.create ? "New policy" : this.name}</h1>
        ${kindSchema?.restart
          ? html`<span class="crumb">
              A change to this kind is restart-class: publishing it restarts the instance's task and
              drops in-flight queries.
            </span>`
          : nothing}
      </div>
      <div class="head-actions">
        ${!this.create && mode
          ? html`<bn-badge .variant=${enforcementModeVariant(enabled ? mode : "Disabled")}>
              ${enabled ? mode : "Disabled"}
            </bn-badge>`
          : nothing}
        ${this.dirty ? html`<span class="dirty">unsaved changes</span>` : nothing}
        ${!this.create
          ? html`<bn-button size="sm" @click=${() => void this.setEnabled(!enabled)}>
                ${enabled ? "Exclude from render" : "Include in render"}
              </bn-button>
              <bn-button size="sm" variant="danger" @click=${() => (this.deleteOpen = true)}>
                Delete
              </bn-button>`
          : nothing}
        <bn-button
          variant="primary"
          ?loading=${this.saving}
          ?disabled=${!this.activeKind || this.blockingSave.length > 0}
          @click=${() => void this.save()}
        >
          ${this.create ? "Create in draft" : "Save to draft"}
        </bn-button>
      </div>
    </header>`;
  }

  private renderDeleteModal() {
    return html`<bn-modal
      heading=${`Delete ${this.name}?`}
      tone="destructive"
      ?open=${this.deleteOpen}
      @close=${() => (this.deleteOpen = false)}
    >
      <p>
        This removes the object from the draft. Nothing changes on the running instance until the draft
        is published.
      </p>
      <p slot="consequence">
        If other objects reference this one — a classification tag, a catalog name — removing it can
        make the draft unpublishable until they are fixed. To stop it acting without removing it, use
        <strong>Exclude from render</strong> instead.
      </p>
      <bn-button slot="footer" @click=${() => (this.deleteOpen = false)}>Cancel</bn-button>
      <bn-button slot="footer" variant="danger" @click=${() => void this.deleteFromDraft()}>
        Delete from draft
      </bn-button>
    </bn-modal>`;
  }

  override render() {
    if (this.loading) {
      return html`<bn-spinner label="Loading the policy editor"></bn-spinner>`;
    }
    if (this.error) {
      return html`<bn-card heading="Could not open this policy">
        <div role="alert">
          <p class="error-message">${this.error.message}</p>
          <bn-kv
            .rows=${[
              { label: "Error code", value: this.error.code },
              ...(this.error.requestId ? [{ label: "Request id", value: this.error.requestId }] : []),
            ]}
          ></bn-kv>
          <div class="error-actions">
            <bn-button variant="primary" @click=${() => void this.boot()}>Try again</bn-button>
            <bn-button @click=${() => navigate(hrefFor("instances", this.instanceId, "policies"))}>
              Back to policies
            </bn-button>
          </div>
        </div>
      </bn-card>`;
    }
    const schema = this.schema;
    if (!schema) return nothing;
    const kindSchema = this.activeKind ? schema.kind(this.activeKind) : undefined;
    const counts = countFindings(this.findings);
    const tabs: BnTab[] = [
      { id: "yaml", label: "YAML" },
      {
        id: "findings",
        label:
          counts.errors + counts.blocking + counts.warnings > 0
            ? `Findings (${counts.errors + counts.blocking + counts.warnings})`
            : "Findings",
      },
      // `SC-F708`. It is a tab beside the YAML and the findings because it answers
      // the third question an author has about an unsaved edit — the linter says
      // whether it can be stored, the YAML says what it will look like, and this says
      // what the engine will do with it.
      { id: "test", label: "Test" },
    ];

    return html`
      ${this.renderHeader()}
      <div class="columns">
        <div class="authored">
          ${this.renderKindPicker()}
          ${this.activeKind
            ? html`<section>
                  <h2>Identity</h2>
                  <policy-envelope
                    .schema=${schema}
                    .kind=${this.activeKind}
                    .metadata=${this.metadata}
                    .mode=${this.mode}
                    ?create=${this.create}
                    ?mode-locked=${!this.create}
                    .enforcement=${!!kindSchema?.enforcement}
                    .enabled=${this.object?.enabled ?? true}
                    .peers=${this.peers}
                    .findings=${this.findings}
                    @envelope-change=${this.onEnvelope}
                    @envelope-promote=${() => void this.openPromote()}
                  ></policy-envelope>
                </section>
                ${this.renderBody()}`
            : html`<p class="lede">Choose a kind to start.</p>`}
        </div>
        <div class="derived">
          <bn-card>
            <bn-tabs
              label="Derived views"
              .tabs=${tabs}
              .active=${this.pane}
              @change=${(e: CustomEvent<BnTabsChangeDetail>) => {
                e.stopPropagation();
                this.pane = e.detail.id;
                if (this.pane === "test") void this.loadTestSuites();
              }}
            >
              <div class="pane" slot="yaml">${this.renderYamlPane()}</div>
              <div class="pane" slot="findings">${this.renderFindingsPane()}</div>
              <div class="pane" slot="test">${this.renderTestPane()}</div>
            </bn-tabs>
          </bn-card>
        </div>
      </div>
      ${this.renderPromoteModal()} ${this.renderDeleteModal()}
    `;
  }
}

/**
 * refList renders an `ObjectRef[]` as the `Kind/name` spelling the linter and every
 * message in the API use.
 *
 * The empty case reads "none" rather than rendering an empty cell, because these
 * three rows are how an author learns their editor was stale — an object they meant
 * to create appearing under `replaced` — and a blank is not a statement.
 */
function refList(refs: ObjectRef[]): string {
  return refs.length === 0 ? "none" : refs.map((r) => `${r.kind}/${r.name}`).join(", ");
}

/** isShadow reports whether a mode is one that evaluates and changes no outcome. It
 * reads the schema's own status vocabulary rather than listing `Audit` and `DryRun`,
 * so a fourth shadow mode would need no change here. */
function isShadow(mode: string, schema: PolicySchema): boolean {
  const effective = mode || schema.enumDefault("EnforcementMode");
  return effective !== "" && effective !== "Enforce";
}

declare global {
  interface HTMLElementTagNameMap {
    "page-policy-editor": PagePolicyEditor;
  }
}
